import moment from 'moment';
import messages from './messages';
import { ASSET_ROW_NAMES, ENUM_ASSET_ROW_NAMES } from './constants';
import {
  getAssetAlternateFormRulesByState,
  hasStateSpecificAssetFormLayout,
} from './stateRules';

import { hasDuplicates } from '../validate';
import generalMessages from '../../App/messages';

const validateValueOrIncome = (value) => {
  return ![null, undefined, ''].includes(value) && +value >= 0;
};

const validateValueLessThan = (value, lessThanValue) => {
  return +value < +lessThanValue;
};

const validateNotEmpty = (value) => {
  return !value;
};

const validateSelectOption = (value) => {
  const stringValue = typeof value === 'string' ? value : '';
  const isDashOnly =
    stringValue.length > 0 &&
    stringValue.split('').every((char) => char === '-');
  return (
    validateNotEmpty(value) ||
    value === 'default' ||
    isDashOnly ||
    +value === -1
  );
};

const validateAccountNumber = (value) => {
  const accountNumberRegex = /^\d{4}$/;
  return !accountNumberRegex.test(value);
};

const getKeyName = (key, row, isList) => {
  if (isList) {
    return key;
  }
  const tail = key.startsWith('asset') && key.length > 5 ? key.slice(5) : key;
  return `${row}Asset${tail.charAt(0).toUpperCase() + tail.slice(1)}`;
};

/**
 * Validates a single asset row, both for the main row and for the list rows,
 * based on the provided parameters and form values.
 *
 * @param {*} assetName a string with the asset name, used to build the keys for validation
 * @param {*} values values of the form, used to validate the specific fields of the asset row
 * @param {*} intl an object with internationalization methods, used to format error messages
 * @param {*} options an object with the options for validation, including:
 * - isHotmaActive: a boolean indicating if the HOTMA feature is active, which affects the validation of certain fields
 * - state: a string with the state of the selected property, used to determine if bank data validation is needed
 * - isList: a boolean indicating if the validation is for a list row, which changes the keys that are validated
 * - isAltForm: a boolean indicating if the alternative form is being used, which changes the fields that are validated
 * - assetRowList: an array with the list of asset rows, used to check for duplicates in "Other" rows
 * @returns an object with the validation errors for the asset row
 */
const validateRow = (
  assetName,
  values,
  intl,
  { isHotmaActive, state, stateRules, isList, isAltForm, assetRowList = [] },
) => {
  const errors = {};

  const isFederalTaxRefundOrCredit =
    assetName === ENUM_ASSET_ROW_NAMES.FEDERAL_TAX_REFUND_OR_CREDIT;
  const isCashHeld = assetName === ENUM_ASSET_ROW_NAMES.CASH_HELD;
  const shouldValidateBankData =
    isHotmaActive &&
    !isFederalTaxRefundOrCredit &&
    !isCashHeld &&
    state === 'US-CA';

  if (!isAltForm) {
    const assetValueKey = `${assetName}AssetValue`;
    const assetIncomeKey = `${assetName}AssetIncome`;

    [assetValueKey, assetIncomeKey].forEach((key) => {
      if (!validateValueOrIncome(values[key])) {
        errors[key] = intl.formatMessage(messages.valueOrIncomeRequired);
      }
      if (values[key] < 0) {
        errors[key] = intl.formatMessage(messages.mustBePositive);
      }
    });
    return errors;
  }

  const marketValueKey = getKeyName('marketValue', assetName, isList);
  const interestRateKey = getKeyName('interestRate', assetName, isList);
  const feesToConvertCashKey = getKeyName(
    'feesToConvertCash',
    assetName,
    isList,
  );
  [marketValueKey, interestRateKey, feesToConvertCashKey].forEach((key) => {
    if (!validateValueOrIncome(values[key])) {
      errors[key] = intl.formatMessage(messages.valueOrIncomeRequired);
    }
    if (values[key] < 0) {
      errors[key] = intl.formatMessage(messages.mustBePositive);
    }
  });

  const actualImputeKey = getKeyName('actualImpute', assetName, isList);
  if (
    isHotmaActive &&
    !isFederalTaxRefundOrCredit &&
    validateSelectOption(values[actualImputeKey])
  ) {
    errors[actualImputeKey] = intl.formatMessage(messages.valueRequired);
  }

  const assetVerificationKey = getKeyName(
    'assetVerification',
    assetName,
    isList,
  );
  if (
    stateRules?.requiresAssetVerification &&
    !isFederalTaxRefundOrCredit &&
    validateSelectOption(values[assetVerificationKey])
  ) {
    errors[assetVerificationKey] = intl.formatMessage(
      messages.assetVerificationRequired,
    );
  }

  const bankSourceKey = getKeyName('bankSource', assetName, isList);
  if (shouldValidateBankData && validateNotEmpty(values[bankSourceKey])) {
    errors[bankSourceKey] = intl.formatMessage(messages.valueRequired);
  }

  const accountNumberKey = getKeyName('accountNumber', assetName, isList);
  if (
    shouldValidateBankData &&
    validateAccountNumber(values[accountNumberKey])
  ) {
    errors[accountNumberKey] = intl.formatMessage(
      messages.invalidAccountNumber,
    );
  }

  if (
    validateValueLessThan(values[marketValueKey], values[feesToConvertCashKey])
  ) {
    errors[feesToConvertCashKey] = intl.formatMessage(
      messages.mustBeLessThanMarketValue,
    );
  }

  if (!isList) {
    return errors;
  }

  // List-only validations
  if (
    [
      ENUM_ASSET_ROW_NAMES.REAL_ESTATE,
      ENUM_ASSET_ROW_NAMES.RENTAL_PROPERTY,
    ].includes(assetName) &&
    validateNotEmpty(values['suitableForOccupancy'])
  ) {
    errors['suitableForOccupancy'] = intl.formatMessage(
      messages.suitableForOccupancyRequired,
    );
  }

  if (
    assetName === ENUM_ASSET_ROW_NAMES.OTHER &&
    validateNotEmpty(values['otherName'])
  ) {
    errors['otherName'] = intl.formatMessage(messages.nameRequired);
  }

  if (
    assetName === ENUM_ASSET_ROW_NAMES.OTHER &&
    stateRules?.requiresOtherAssetType &&
    validateSelectOption(values['otherAssetType'])
  ) {
    errors['otherAssetType'] = intl.formatMessage(messages.valueRequired);
  }

  if (
    assetName === ENUM_ASSET_ROW_NAMES.INVESTMENT_PROPERTIES &&
    stateRules?.requiresInvestmentPropertyType &&
    validateSelectOption(values['investmentPropertyType'])
  ) {
    errors['investmentPropertyType'] = intl.formatMessage(
      messages.valueRequired,
    );
  }

  if (
    assetName === ENUM_ASSET_ROW_NAMES.OTHER &&
    hasDuplicates(assetRowList, values['otherName'], 'otherName')
  ) {
    errors['otherName'] = intl.formatMessage(
      generalMessages.duplicateIdentifiers,
      { item: '"Other" rows', identifiers: 'names' },
    );
  }

  return errors;
};

/**
 * Validates the Asset Information Form by checking if the required fields are filled out.
 *
 * @param {*} values a json with the form values
 * @param {*} props an object with the props comming from the AssetInformation component
 * @returns an object with or without errors
 */
export const validate = (
  values: Object,
  { intl, isHotmaActive, flags, selectedProperty, programName }: Object,
) => {
  let errors = {};
  const { assetsTaxRefund } = flags;
  const state = selectedProperty?.physicalState;
  const isAltForm = values?.isAltForm ?? false;
  const shouldUseAlternateLayout = hasStateSpecificAssetFormLayout(
    state,
    programName,
    flags,
    isHotmaActive ?? false,
  );
  const hasAlternateColumns = isAltForm || shouldUseAlternateLayout;
  const stateRules = getAssetAlternateFormRulesByState(
    state,
    programName,
    flags,
    isHotmaActive ?? false,
  );
  const assets = ASSET_ROW_NAMES.filter((asset) =>
    assetsTaxRefund
      ? true
      : asset !== ENUM_ASSET_ROW_NAMES.FEDERAL_TAX_REFUND_OR_CREDIT,
  );

  assets.forEach((row) => {
    if (hasAlternateColumns && !values[row]) {
      errors[row] = intl.formatMessage(messages.mustBePositive);
    }

    if (values[row] !== 'yes') {
      return;
    }

    const assetRowList = values[`${row}List`];
    const isList = Array.isArray(assetRowList);

    if (isList) {
      const assetErrors = assetRowList.map((assetRow) => {
        return validateRow(row, assetRow, intl, {
          state,
          stateRules,
          isAltForm: hasAlternateColumns,
          isHotmaActive,
          isList: true,
          assetRowList,
        });
      });

      if (assetErrors.some((error) => Object.keys(error).length > 0)) {
        errors[`${row}List`] = assetErrors;
      }
    } else {
      errors = {
        ...errors,
        ...validateRow(row, values, intl, {
          state,
          stateRules,
          isAltForm: hasAlternateColumns,
          isHotmaActive,
          isList: false,
        }),
      };
    }
  });

  if (values.disposedDate) {
    const disposedDate = moment(values.disposedDate);
    if (!disposedDate.isValid()) {
      errors['disposedDate'] = intl.formatMessage(messages.invalidDate);
    } else {
      const today = moment();
      const threeYearsPrior = moment().subtract(3, 'years');
      if (!disposedDate.isBetween(threeYearsPrior, today, null, '[]')) {
        errors['disposedDate'] = intl.formatMessage(messages.invalidDate);
      }
    }
  }

  return errors;
};

export default validate;
